import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DataObjectIcon from '@mui/icons-material/DataObject';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  IPdfBlockTemplate,
  IPdfBlockElement,
  DEFAULT_BLOCK_TEMPLATE,
  BLOCK_SIZE_PRESETS,
  BLOCK_ELEMENT_PRESETS,
} from '../../types/pdfBlockTypes';
import { PdfElementKind, ELEMENT_KIND_LABELS } from '../../types/pdfTemplateTypes';
import {
  getPdfBlocks,
  createPdfBlock,
  updatePdfBlock,
  deletePdfBlock,
  duplicatePdfBlock,
} from '../../services/pdfBlockService';
import axiosInstance from '../../services/axiosInstance';
import { IAsset, ITemplate } from '../../types/damTypes';
import {
  buildAvailableFields,
  getAssetMetadataValue,
  renderRichTextHtml,
} from '../../features/pdfDesigner/pdfDesignerUtils';

// Scale factor for canvas preview. 96 DPI is ~3.78 px/mm; 4 keeps 100% readable.
const MM_TO_PX_CANVAS = 4;
const MIN_ELEMENT_SIZE_MM = 2;

// Element icons
const ELEMENT_ICONS: Record<PdfElementKind, React.ReactNode> = {
  logo: <ImageIcon fontSize="small" />,
  productImage: <ImageIcon fontSize="small" />,
  textField: <DataObjectIcon fontSize="small" />,
  staticText: <TextFieldsIcon fontSize="small" />,
  richText: <TextFieldsIcon fontSize="small" />,
  rectangle: <span style={{ fontSize: 14 }}>▢</span>,
  line: <span style={{ fontSize: 14 }}>—</span>,
  staticImage: <ImageIcon fontSize="small" />,
  bulletList: <span style={{ fontSize: 14 }}>•</span>,
};

const PANEL_SX = {
  borderRadius: 3,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
};

const TOOLBAR_SURFACE_SX = {
  p: 1.25,
  borderRadius: 2.5,
  border: '1px solid rgba(27, 57, 103, 0.08)',
  background: 'rgba(255,255,255,0.92)',
  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
};

const LIST_ITEM_SURFACE_SX = {
  mb: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(27, 57, 103, 0.08)',
  backgroundColor: 'rgba(255,255,255,0.7)',
  '&.Mui-selected': {
    backgroundColor: 'rgba(27, 57, 103, 0.1)',
    borderColor: 'rgba(27, 57, 103, 0.22)',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(27, 57, 103, 0.14)',
  },
};

// Generate unique ID
const generateId = () => `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Normalize element to ensure style exists
function normalizeElement(el: Partial<IPdfBlockElement>): IPdfBlockElement {
  return {
    ...el,
    style: el.style || {},
  } as IPdfBlockElement;
}

// Normalize block template
function normalizeBlock(block: Partial<IPdfBlockTemplate>): IPdfBlockTemplate {
  return {
    ...DEFAULT_BLOCK_TEMPLATE,
    ...block,
    elements: (block.elements || []).map(normalizeElement),
  } as IPdfBlockTemplate;
}

// Drag/resize types
type DragMode =
  | 'move'
  | 'resize-n'
  | 'resize-s'
  | 'resize-e'
  | 'resize-w'
  | 'resize-ne'
  | 'resize-nw'
  | 'resize-se'
  | 'resize-sw'
  | null;

const ProductBlockEditor: React.FC = () => {
  // State
  const [blocks, setBlocks] = useState<IPdfBlockTemplate[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<IPdfBlockTemplate | null>(null);
  const [selectedElement, setSelectedElement] = useState<IPdfBlockElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; block: IPdfBlockTemplate | null }>({
    open: false,
    block: null,
  });
  const [expandedElements, setExpandedElements] = useState(true);
  const [expandedProperties, setExpandedProperties] = useState(true);
  const [expandedPresets, setExpandedPresets] = useState(false);

  // Zoom
  const [zoom, setZoom] = useState(1);

  // Template fields for metadata mapping
  const [fieldTemplates, setFieldTemplates] = useState<ITemplate[]>([]);
  const [fieldTemplatesLoading, setFieldTemplatesLoading] = useState(false);
  const [previewAssetId, setPreviewAssetId] = useState('');
  const [previewAsset, setPreviewAsset] = useState<IAsset | null>(null);
  const [previewAssetSearch, setPreviewAssetSearch] = useState('');
  const [previewAssetOptions, setPreviewAssetOptions] = useState<IAsset[]>([]);
  const [staticImageSearch, setStaticImageSearch] = useState('');
  const [staticImageOptions, setStaticImageOptions] = useState<IAsset[]>([]);
  const [blockTypeFilter, setBlockTypeFilter] = useState<'all' | 'products' | 'wine'>('all');
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(320);
  const rightPanelResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [isResizingRightPanel, setIsResizingRightPanel] = useState(false);

  // Grid / snapping + undo/redo
  const [gridSizeMm, setGridSizeMm] = useState<number>(5);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(false);
  const undoStackRef = useRef<IPdfBlockTemplate[]>([]);
  const redoStackRef = useRef<IPdfBlockTemplate[]>([]);
  const prevBlockRef = useRef<IPdfBlockTemplate | null>(null);
  const isHistoryActionRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const gridSizeMmRef = useRef<number>(gridSizeMm);
  const snapToGridRef = useRef<boolean>(snapToGrid);
  const pxPerMmRef = useRef<number>(MM_TO_PX_CANVAS * zoom);
  const selectedBlockRef = useRef<IPdfBlockTemplate | null>(selectedBlock);
  useEffect(() => { gridSizeMmRef.current = gridSizeMm; }, [gridSizeMm]);
  useEffect(() => { snapToGridRef.current = snapToGrid; }, [snapToGrid]);
  useEffect(() => { pxPerMmRef.current = MM_TO_PX_CANVAS * zoom; }, [zoom]);
  useEffect(() => { selectedBlockRef.current = selectedBlock; }, [selectedBlock]);

  // Drag/resize state
  const canvasAreaRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    mode: DragMode;
    elementId: string | null;
    startX: number;
    startY: number;
    startElX: number;
    startElY: number;
    startElW: number;
    startElH: number;
  }>({ mode: null, elementId: null, startX: 0, startY: 0, startElX: 0, startElY: 0, startElW: 0, startElH: 0 });

  const withImgToken = useCallback((url: string) => {
    if (!url || !url.startsWith('/api/')) return url;
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}token=${encodeURIComponent(token)}`;
    } catch {
      return url;
    }
  }, []);

  const loadBlocks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPdfBlocks(blockTypeFilter === 'all' ? undefined : blockTypeFilter);
      const normalized = data.map(normalizeBlock);
      setBlocks(normalized);
      setSelectedBlock((prev) => {
        if (normalized.length === 0) return prev;
        if (!prev || !normalized.some((block) => block._id === prev._id)) {
          return normalized[0];
        }
        return prev;
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Kunde inte ladda block', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [blockTypeFilter]);

  const loadFieldTemplates = useCallback(async () => {
    try {
      setFieldTemplatesLoading(true);
      const { data } = await axiosInstance.get('/api/templates');
      setFieldTemplates(data || []);
    } catch (err) {
      console.error('Failed to load field templates:', err);
    } finally {
      setFieldTemplatesLoading(false);
    }
  }, []);

  // Load blocks
  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  // Load metadata templates/fields for mapping
  useEffect(() => {
    loadFieldTemplates();
  }, [loadFieldTemplates]);

  useEffect(() => {
    if (!previewAssetSearch || previewAssetSearch.length < 2) {
      setPreviewAssetOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets', {
          params: { search: previewAssetSearch, limit: 20 },
        });
        setPreviewAssetOptions(data.assets || data || []);
      } catch (err) {
        console.error('Preview asset search error:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [previewAssetSearch]);

  useEffect(() => {
    if (!staticImageSearch || staticImageSearch.length < 2) {
      setStaticImageOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets', {
          params: { search: staticImageSearch, limit: 20, fileType: 'image' },
        });
        setStaticImageOptions(data.assets || data || []);
      } catch (err) {
        console.error('Static image search error:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [staticImageSearch]);

  useEffect(() => {
    if (!previewAssetId) {
      setPreviewAsset(null);
      return;
    }
    const loadAsset = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/assets/${previewAssetId}`);
        setPreviewAsset(data);
      } catch (err) {
        console.error('Error loading preview asset:', err);
        setPreviewAsset(null);
      }
    };
    loadAsset();
  }, [previewAssetId]);

  const availableFields = useMemo(() => {
    return buildAvailableFields(fieldTemplates, selectedBlock?.type || 'products');
  }, [fieldTemplates, selectedBlock?.type]);

  const cloneBlock = useCallback((b: IPdfBlockTemplate): IPdfBlockTemplate => {
    return JSON.parse(JSON.stringify(b)) as IPdfBlockTemplate;
  }, []);

  // Track history per selected block (switching block resets stacks)
  useEffect(() => {
    if (!selectedBlock) {
      undoStackRef.current = [];
      redoStackRef.current = [];
      prevBlockRef.current = null;
      setCanUndo(false);
      setCanRedo(false);
      return;
    }

    const prev = prevBlockRef.current;
    if (!prev) {
      prevBlockRef.current = cloneBlock(selectedBlock);
      return;
    }

    const prevKey = String((prev as any)._id || 'new');
    const currKey = String((selectedBlock as any)._id || 'new');
    if (prevKey !== currKey) {
      undoStackRef.current = [];
      redoStackRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
      prevBlockRef.current = cloneBlock(selectedBlock);
      return;
    }

    if (isHistoryActionRef.current) {
      isHistoryActionRef.current = false;
      prevBlockRef.current = cloneBlock(selectedBlock);
      return;
    }

    undoStackRef.current.push(prev);
    if (undoStackRef.current.length > 60) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(false);
    prevBlockRef.current = cloneBlock(selectedBlock);
  }, [selectedBlock, cloneBlock]);

  const undo = useCallback(() => {
    if (!selectedBlock) return;
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const prev = stack.pop()!;
    redoStackRef.current.push(cloneBlock(selectedBlock));
    setCanUndo(stack.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    isHistoryActionRef.current = true;
    setSelectedElement(null);
    setSelectedBlock(prev);
  }, [selectedBlock, cloneBlock]);

  const redo = useCallback(() => {
    if (!selectedBlock) return;
    const stack = redoStackRef.current;
    if (stack.length === 0) return;
    const next = stack.pop()!;
    undoStackRef.current.push(cloneBlock(selectedBlock));
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(stack.length > 0);
    isHistoryActionRef.current = true;
    setSelectedElement(null);
    setSelectedBlock(next);
  }, [selectedBlock, cloneBlock]);

  useEffect(() => {
    if (selectedElement) {
      setExpandedProperties(true);
    }
  }, [selectedElement]);

  // Create new block
  const handleCreateBlock = () => {
    const newBlock: IPdfBlockTemplate = {
      ...DEFAULT_BLOCK_TEMPLATE,
      name: 'Nytt produktblock',
      type: blockTypeFilter === 'all' ? 'products' : blockTypeFilter,
    };
    setSelectedBlock(newBlock);
    setSelectedElement(null);
  };

  // Save block
  const handleSaveBlock = useCallback(async () => {
    if (!selectedBlock) return;
    try {
      setSaving(true);
      let saved: IPdfBlockTemplate;
      if (selectedBlock._id) {
        saved = await updatePdfBlock(selectedBlock._id, selectedBlock);
      } else {
        saved = await createPdfBlock(selectedBlock);
      }
      saved = normalizeBlock(saved);
      setBlocks((prev) => {
        const idx = prev.findIndex((b) => b._id === saved._id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });
      setSelectedBlock(saved);
      setSnackbar({ open: true, message: 'Block sparat!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Kunde inte spara block', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [selectedBlock]);

  // Delete block
  const handleDeleteBlock = async () => {
    const block = deleteDialog.block;
    if (!block?._id) {
      setDeleteDialog({ open: false, block: null });
      return;
    }
    try {
      await deletePdfBlock(block._id);
      setBlocks((prev) => prev.filter((b) => b._id !== block._id));
      if (selectedBlock?._id === block._id) {
        setSelectedBlock(blocks.length > 1 ? blocks.find((b) => b._id !== block._id) || null : null);
      }
      setSnackbar({ open: true, message: 'Block raderat', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Kunde inte radera block', severity: 'error' });
    } finally {
      setDeleteDialog({ open: false, block: null });
    }
  };

  // Duplicate block
  const handleDuplicateBlock = async (block: IPdfBlockTemplate) => {
    if (!block._id) return;
    try {
      const dup = await duplicatePdfBlock(block._id);
      const normalized = normalizeBlock(dup);
      setBlocks((prev) => [...prev, normalized]);
      setSelectedBlock(normalized);
      setSnackbar({ open: true, message: 'Block duplicerat', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Kunde inte duplicera', severity: 'error' });
    }
  };

  // Add element to block
  const handleAddElement = (kind: PdfElementKind) => {
    if (!selectedBlock) return;
    const preset = BLOCK_ELEMENT_PRESETS[kind] || {
      kind,
      xMm: 5,
      yMm: 5,
      wMm: 30,
      hMm: 10,
      style: {},
    };
    const newElement: IPdfBlockElement = {
      id: generateId(),
      ...preset,
      kind,
    };
    const updated = {
      ...selectedBlock,
      elements: [...selectedBlock.elements, newElement],
    };
    setSelectedBlock(updated);
    setSelectedElement(newElement);
    setExpandedProperties(true);
  };

  // Update element
  const updateElement = useCallback(
    (id: string, changes: Partial<IPdfBlockElement>) => {
      if (!selectedBlock) return;
      const updated = {
        ...selectedBlock,
        elements: selectedBlock.elements.map((el) =>
          el.id === id ? { ...el, ...changes } : el
        ),
      };
      setSelectedBlock(updated);
      if (selectedElement?.id === id) {
        setSelectedElement({ ...selectedElement, ...changes });
      }
    },
    [selectedBlock, selectedElement]
  );

  // Delete element
  const handleDeleteElement = useCallback((id: string) => {
    if (!selectedBlock) return;
    setSelectedBlock({
      ...selectedBlock,
      elements: selectedBlock.elements.filter((el) => el.id !== id),
    });
    if (selectedElement?.id === id) {
      setSelectedElement(null);
    }
  }, [selectedBlock, selectedElement?.id]);

  // Update block field
  const updateBlockField = (field: keyof IPdfBlockTemplate, value: any) => {
    if (!selectedBlock) return;
    setSelectedBlock({ ...selectedBlock, [field]: value });
  };

  // ========== Drag & Resize Handlers ==========
  const handlePointerMove = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag.mode || !drag.elementId) return;
    const block = selectedBlockRef.current;
    if (!block) return;

    const pxPerMm = pxPerMmRef.current || MM_TO_PX_CANVAS;
    const deltaX = (e.clientX - drag.startX) / pxPerMm;
    const deltaY = (e.clientY - drag.startY) / pxPerMm;

    const snapEnabled = snapToGridRef.current && !e.altKey;
    const grid = Math.max(0.1, Number(gridSizeMmRef.current || 0.1));
    const maybeSnap = (v: number) => (snapEnabled ? Math.round(v / grid) * grid : v);
    const round1 = (v: number) => Math.round(v * 10) / 10;

    let changes: Partial<IPdfBlockElement> = {};

    if (drag.mode === 'move') {
      const maxX = Math.max(0, block.wMm - drag.startElW);
      const maxY = Math.max(0, block.hMm - drag.startElH);
      changes = {
        xMm: Math.min(maxX, Math.max(0, round1(maybeSnap(drag.startElX + deltaX)))),
        yMm: Math.min(maxY, Math.max(0, round1(maybeSnap(drag.startElY + deltaY)))),
      };
    } else {
      const touchesEast = drag.mode.includes('e');
      const touchesWest = drag.mode.includes('w');
      const touchesSouth = drag.mode.includes('s');
      const touchesNorth = drag.mode.includes('n');
      const maxW = Math.max(MIN_ELEMENT_SIZE_MM, block.wMm - drag.startElX);
      const maxH = Math.max(MIN_ELEMENT_SIZE_MM, block.hMm - drag.startElY);
      const startRight = drag.startElX + drag.startElW;
      const startBottom = drag.startElY + drag.startElH;

      if (touchesEast) {
        changes.wMm = Math.min(maxW, Math.max(MIN_ELEMENT_SIZE_MM, round1(maybeSnap(drag.startElW + deltaX))));
      }
      if (touchesSouth) {
        changes.hMm = Math.min(maxH, Math.max(MIN_ELEMENT_SIZE_MM, round1(maybeSnap(drag.startElH + deltaY))));
      }
      if (touchesWest) {
        const maxX = Math.min(startRight - MIN_ELEMENT_SIZE_MM, block.wMm - MIN_ELEMENT_SIZE_MM);
        const nextX = Math.min(maxX, Math.max(0, round1(maybeSnap(drag.startElX + deltaX))));
        changes.xMm = nextX;
        changes.wMm = Math.max(MIN_ELEMENT_SIZE_MM, round1(startRight - nextX));
      }
      if (touchesNorth) {
        const maxY = Math.min(startBottom - MIN_ELEMENT_SIZE_MM, block.hMm - MIN_ELEMENT_SIZE_MM);
        const nextY = Math.min(maxY, Math.max(0, round1(maybeSnap(drag.startElY + deltaY))));
        changes.yMm = nextY;
        changes.hMm = Math.max(MIN_ELEMENT_SIZE_MM, round1(startBottom - nextY));
      }
    }

    setSelectedBlock((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        elements: prev.elements.map((el) =>
          el.id === drag.elementId ? { ...el, ...changes } : el
        ),
      };
    });

    setSelectedElement((prev) => {
      if (!prev || prev.id !== drag.elementId) return prev;
      return { ...prev, ...changes };
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = {
      mode: null,
      elementId: null,
      startX: 0,
      startY: 0,
      startElX: 0,
      startElY: 0,
      startElW: 0,
      startElH: 0,
    };
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    document.removeEventListener('pointercancel', handlePointerUp);
  }, [handlePointerMove]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, elementId: string, mode: DragMode) => {
      e.stopPropagation();
      e.preventDefault();
      if (!selectedBlock) return;

      const el = selectedBlock.elements.find((x) => x.id === elementId);
      if (!el) return;

      dragRef.current = {
        mode,
        elementId,
        startX: e.clientX,
        startY: e.clientY,
        startElX: el.xMm,
        startElY: el.yMm,
        startElW: el.wMm,
        startElH: el.hMm,
      };

      setSelectedElement(el);
      const cursorByMode: Record<Exclude<DragMode, null>, string> = {
        move: 'move',
        'resize-n': 'n-resize',
        'resize-s': 's-resize',
        'resize-e': 'e-resize',
        'resize-w': 'w-resize',
        'resize-ne': 'ne-resize',
        'resize-nw': 'nw-resize',
        'resize-se': 'se-resize',
        'resize-sw': 'sw-resize',
      };
      document.body.style.cursor = mode ? cursorByMode[mode] : '';
      document.body.style.userSelect = 'none';

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
    },
    [selectedBlock, handlePointerMove, handlePointerUp]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // Right panel resize handlers (drag left edge to resize)
  useEffect(() => {
    if (!isResizingRightPanel) return;

    const onMove = (e: MouseEvent) => {
      const start = rightPanelResizeRef.current;
      if (!start) return;
      const delta = start.startX - e.clientX;
      setRightPanelWidth(Math.min(720, Math.max(280, start.startWidth + delta)));
    };

    const onUp = () => {
      setIsResizingRightPanel(false);
      rightPanelResizeRef.current = null;
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizingRightPanel]);

  // Keyboard shortcuts (avoid when typing in inputs)
  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const isCtrl = e.ctrlKey || e.metaKey;
      const key = (e.key || '').toLowerCase();

      if (isCtrl && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (isCtrl && (key === 'y' || (key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      if (isCtrl && key === 's') {
        e.preventDefault();
        handleSaveBlock();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElement) {
          e.preventDefault();
          handleDeleteElement(selectedElement.id);
        }
        return;
      }

      const isArrow =
        e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (!isArrow || !selectedElement) return;

      const step = e.shiftKey ? 5 : 1; // mm
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      if (dx === 0 && dy === 0) return;

      e.preventDefault();
      updateElement(selectedElement.id, {
        xMm: Math.max(0, Math.round((selectedElement.xMm + dx) * 10) / 10),
        yMm: Math.max(0, Math.round((selectedElement.yMm + dy) * 10) / 10),
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedElement, undo, redo, updateElement, handleDeleteElement, handleSaveBlock]);

  const pxPerMm = MM_TO_PX_CANVAS * zoom;
  const ptToPx = pxPerMm / MM_TO_PX_CANVAS * (96 / 72);

  const fitBlockToCanvas = useCallback(() => {
    if (!selectedBlock || !canvasAreaRef.current) return;
    const rect = canvasAreaRef.current.getBoundingClientRect();
    const availableW = Math.max(120, rect.width - 96);
    const availableH = Math.max(120, rect.height - 96);
    const nextZoom = Math.min(
      4,
      Math.max(
        0.35,
        Math.min(
          availableW / Math.max(1, selectedBlock.wMm * MM_TO_PX_CANVAS),
          availableH / Math.max(1, selectedBlock.hMm * MM_TO_PX_CANVAS)
        )
      )
    );
    setZoom(Math.round(nextZoom * 100) / 100);
  }, [selectedBlock]);

  const renderCanvasElementContent = useCallback((el: IPdfBlockElement) => {
    const style = el.style || {};
    const previewTextStyle = {
      fontFamily: style.fontFamily || 'Arial',
      fontSize: `${(style.fontSize || 12) * ptToPx}px`,
      fontWeight: style.fontWeight || 'normal',
      fontStyle: style.fontStyle || 'normal',
      color: style.color || '#333',
      textAlign: style.textAlign || 'left',
      lineHeight: style.lineHeight || 'normal',
      width: '100%',
      height: '100%',
      padding: `${(style.padding || 0) * pxPerMm}px`,
      boxSizing: 'border-box' as const,
      textTransform: style.textTransform || 'none',
      letterSpacing: style.letterSpacing ? `${style.letterSpacing * ptToPx}px` : undefined,
    };

    if (el.kind === 'productImage') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems:
              style.imageAlign === 'top' ? 'flex-start' : style.imageAlign === 'bottom' ? 'flex-end' : 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {previewAsset ? (
            <img
              src={withImgToken(`/api/assets/${previewAsset._id}/preview`)}
              alt={previewAsset.title || 'Produktbild'}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (!img.src.includes('/preview/thumbnail')) {
                  img.src = withImgToken(`/api/assets/${previewAsset._id}/preview/thumbnail`);
                }
              }}
            />
          ) : (
            <Box sx={{ color: '#999', textAlign: 'center' }}>
              <ImageIcon />
              <div>Produktbild</div>
            </Box>
          )}
        </Box>
      );
    }

    if (el.kind === 'textField') {
      const prefix = el.binding?.prefix || '';
      const suffix = el.binding?.suffix || '';
      const value = getAssetMetadataValue(previewAsset, el.binding?.metadataKey || '', el.binding?.fallbackValue || `{${el.binding?.metadataKey || 'fält'}}`);
      return (
        <Box
          sx={{
            ...previewTextStyle,
            display: 'flex',
            flexDirection: 'column',
            justifyContent:
              style.verticalAlign === 'middle' ? 'center' : style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
            pointerEvents: 'none',
          }}
        >
          {`${prefix}${value}${suffix}`}
        </Box>
      );
    }

    if (el.kind === 'staticText') {
      return (
        <Box
          sx={{
            ...previewTextStyle,
            display: 'flex',
            flexDirection: 'column',
            justifyContent:
              style.verticalAlign === 'middle' ? 'center' : style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
            pointerEvents: 'none',
          }}
        >
          {el.content || 'Text'}
        </Box>
      );
    }

    if (el.kind === 'richText') {
      return (
        <Box
          sx={{
            ...previewTextStyle,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
          dangerouslySetInnerHTML={{ __html: renderRichTextHtml(el.content || '', previewAsset) }}
        />
      );
    }

    if (el.kind === 'line') {
      return (
        <Box
          sx={{
            width: '100%',
            borderTop: `${Math.max(1, (style.borderWidth || 1) * pxPerMm)}px ${style.borderStyle || 'solid'} ${style.borderColor || '#000'}`,
            pointerEvents: 'none',
          }}
        />
      );
    }

    if (el.kind === 'rectangle') {
      return null;
    }

    if (el.kind === 'logo') {
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <img
            src="/Fontana_Logo_Neg.png"
            alt="Logo"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </Box>
      );
    }

    if (el.kind === 'staticImage') {
      const previewUrl = el.imageUrl?.includes('/api/assets/') && el.imageUrl.includes('/download')
        ? el.imageUrl.replace('/download', '/preview')
        : el.imageUrl;
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          {previewUrl ? (
            <img
              src={withImgToken(previewUrl)}
              alt="Statisk bild"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <PhotoLibraryIcon sx={{ color: '#999' }} />
          )}
        </Box>
      );
    }

    if (el.kind === 'bulletList') {
      return (
        <Box sx={{ ...previewTextStyle, overflow: 'hidden', pointerEvents: 'none' }}>
          <ul style={{ margin: 0, paddingLeft: '1.5em', listStyleType: style.bulletStyle || 'disc' }}>
            {(el.content || '').split('\n').map((line, index) => {
              const cleanLine = line.replace(/^[•◦▪■○●*-]\s*/, '').trim();
              return cleanLine ? <li key={index}>{cleanLine}</li> : null;
            })}
          </ul>
        </Box>
      );
    }

    return null;
  }, [previewAsset, ptToPx, pxPerMm, withImgToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        height: '100%',
        minHeight: 0,
        gap: 1,
        p: 0.75,
        borderRadius: 3,
        background: 'linear-gradient(180deg, #eef4fb 0%, #f6f8fc 100%)',
      }}
    >
      {/* Left panel - Block list */}
      <Paper
        sx={{
          ...PANEL_SX,
          width: { xs: '100%', lg: 280 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          maxHeight: { xs: 320, lg: 'none' },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Produktblock
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Bygg återanvändbara produktsektioner som fungerar i multi-mallar.
          </Typography>
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>Visa typ</InputLabel>
            <Select
              value={blockTypeFilter}
              label="Visa typ"
              onChange={(e) => setBlockTypeFilter(e.target.value as 'all' | 'products' | 'wine')}
            >
              <MenuItem value="all">Alla</MenuItem>
              <MenuItem value="products">Produkter</MenuItem>
              <MenuItem value="wine">Vin</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            fullWidth
            onClick={handleCreateBlock}
          >
            Nytt block
          </Button>
        </Box>
        <List sx={{ flexGrow: 1, overflow: 'auto', px: 1.25, py: 1.25 }}>
          {blocks.map((block) => (
            <ListItem key={block._id} disablePadding>
              <ListItemButton
                selected={selectedBlock?._id === block._id}
                sx={LIST_ITEM_SURFACE_SX}
                onClick={() => {
                  setSelectedBlock(normalizeBlock(block));
                  setSelectedElement(null);
                }}
              >
                <ListItemText
                  primary={block.name}
                  secondary={`${block.wMm}×${block.hMm} mm • ${block.type}`}
                />
              </ListItemButton>
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleDuplicateBlock(block)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setDeleteDialog({ open: true, block })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Center - Canvas */}
      <Paper
        sx={{
          ...PANEL_SX,
          flexGrow: 1,
          minWidth: 0,
          minHeight: { xs: 560, lg: 0 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {selectedBlock ? (
          <>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ ...TOOLBAR_SURFACE_SX, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Blocknamn"
                  value={selectedBlock.name}
                  onChange={(e) => updateBlockField('name', e.target.value)}
                  size="small"
                  sx={{ width: { xs: '100%', sm: 240 } }}
                />
                <FormControl size="small" sx={{ width: { xs: 'calc(50% - 4px)', sm: 130 } }}>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    value={selectedBlock.type}
                    label="Typ"
                    onChange={(e) => updateBlockField('type', e.target.value)}
                  >
                    <MenuItem value="products">Produkter</MenuItem>
                    <MenuItem value="wine">Vin</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Bredd (mm)"
                  type="number"
                  value={selectedBlock.wMm}
                  onChange={(e) => updateBlockField('wMm', Number(e.target.value))}
                  size="small"
                  sx={{ width: { xs: 'calc(50% - 4px)', sm: 110 } }}
                />
                <TextField
                  label="Höjd (mm)"
                  type="number"
                  value={selectedBlock.hMm}
                  onChange={(e) => updateBlockField('hMm', Number(e.target.value))}
                  size="small"
                  sx={{ width: { xs: 'calc(50% - 4px)', sm: 110 } }}
                />
                <Autocomplete
                  size="small"
                  options={previewAssetOptions}
                  getOptionLabel={(option) => option.title || option.fileName || ''}
                  inputValue={previewAssetSearch}
                  onInputChange={(_, value) => setPreviewAssetSearch(value)}
                  onChange={(_, asset) => {
                    if (asset) {
                      setPreviewAssetId(asset._id);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Previewprodukt" placeholder="Sök bild/produkt..." />
                  )}
                  sx={{ width: { xs: '100%', md: 280 }, flexGrow: 1 }}
                />
                {previewAsset && (
                  <Chip
                    size="small"
                    label={previewAsset.title || previewAsset.fileName}
                    onDelete={() => {
                      setPreviewAssetId('');
                      setPreviewAssetSearch('');
                    }}
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ ...TOOLBAR_SURFACE_SX, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`${selectedBlock.wMm} × ${selectedBlock.hMm} mm`}
                  sx={{ bgcolor: 'rgba(27, 57, 103, 0.08)', color: '#1B3967' }}
                />
                <Chip
                  size="small"
                  label={previewAsset ? (previewAsset.title || previewAsset.fileName || 'Preview vald') : 'Ingen previewprodukt'}
                  variant="outlined"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Zooma ut">
                    <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.35, Math.round((z - 0.1) * 100) / 100))}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
                    {Math.round(zoom * 100)}%
                  </Typography>
                  <Tooltip title="Zooma in">
                    <IconButton size="small" onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.1) * 100) / 100))}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" variant="outlined" onClick={fitBlockToCanvas}>
                    Passa
                  </Button>
                </Box>
                <FormControl size="small" sx={{ width: 110 }}>
                  <InputLabel>Rutnät</InputLabel>
                  <Select
                    value={gridSizeMm}
                    label="Rutnät"
                    onChange={(e) => setGridSizeMm(Number(e.target.value) || 5)}
                  >
                    <MenuItem value={1}>1 mm</MenuItem>
                    <MenuItem value={2}>2 mm</MenuItem>
                    <MenuItem value={5}>5 mm</MenuItem>
                    <MenuItem value={10}>10 mm</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant={snapToGrid ? 'contained' : 'outlined'}
                  onClick={() => setSnapToGrid((v) => !v)}
                  sx={{ minWidth: 64 }}
                >
                  Snap
                </Button>
                <Tooltip title="Ångra (Ctrl+Z)">
                  <span>
                    <IconButton size="small" onClick={undo} disabled={!canUndo}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Gör om (Ctrl+Y)">
                  <span>
                    <IconButton size="small" onClick={redo} disabled={!canRedo}>
                      <RedoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBlock}
                  disabled={saving}
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </Button>
              </Box>
            </Box>

            {/* Canvas area */}
            <Box
              ref={canvasAreaRef}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                background: 'radial-gradient(circle at top, rgba(74,123,167,0.16), rgba(224,224,224,1) 38%)',
                borderRadius: 2.5,
              }}
            >
              <Box
                sx={{
                  minWidth: '100%',
                  minHeight: '100%',
                  width: Math.max(selectedBlock.wMm * pxPerMm + 96, 0),
                  height: Math.max(selectedBlock.hMm * pxPerMm + 96, 0),
                  p: 6,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  boxSizing: 'border-box',
                }}
              >
              <Box
                ref={canvasRef}
                sx={{
                  position: 'relative',
                  width: selectedBlock.wMm * pxPerMm,
                  height: selectedBlock.hMm * pxPerMm,
                  bgcolor: 'white',
                  boxShadow: 3,
                  border: '1px solid #ccc',
                }}
              >
                {/* Grid overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `
                      linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)
                    `,
                    backgroundSize: `${gridSizeMm * pxPerMm}px ${gridSizeMm * pxPerMm}px`,
                    pointerEvents: 'none',
                  }}
                />

                {/* Render elements */}
                {[...selectedBlock.elements]
                  .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                  .map((el) => {
                    const style = el.style || {};
                    const isSelected = selectedElement?.id === el.id;
                    return (
                      <Box
                        key={el.id}
                        onPointerDown={(e) => handlePointerDown(e, el.id, 'move')}
                        sx={{
                          position: 'absolute',
                          left: el.xMm * pxPerMm,
                          top: el.yMm * pxPerMm,
                          width: el.wMm * pxPerMm,
                          height: el.hMm * pxPerMm,
                          border: isSelected ? '2px solid #1976d2' : '1px dashed rgba(0,0,0,0.3)',
                          cursor: 'move',
                          overflow: 'visible',
                          bgcolor: style.backgroundColor || 'transparent',
                          borderRadius: (style.borderRadius || 0) * pxPerMm,
                          borderWidth: style.borderWidth ? `${style.borderWidth * pxPerMm}px` : undefined,
                          borderStyle: style.borderWidth ? (style.borderStyle || 'solid') : undefined,
                          borderColor: style.borderWidth ? (style.borderColor || '#000') : undefined,
                          opacity: style.opacity ?? 1,
                          transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                          transformOrigin: 'center center',
                          '&:hover': { borderColor: '#1976d2' },
                          userSelect: 'none',
                          touchAction: 'none',
                        }}
                      >
                        {/* Resize handles - only show when selected */}
                        {isSelected && (
                          <>
                            {/* Edges */}
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-n')}
                              sx={{
                                position: 'absolute',
                                top: -5,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 18,
                                height: 10,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'n-resize',
                                borderRadius: 1,
                                zIndex: 5,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-s')}
                              sx={{
                                position: 'absolute',
                                bottom: -5,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 18,
                                height: 10,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 's-resize',
                                borderRadius: 1,
                                zIndex: 5,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-e')}
                              sx={{
                                position: 'absolute',
                                right: -5,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 10,
                                height: 18,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'e-resize',
                                borderRadius: 1,
                                zIndex: 5,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-w')}
                              sx={{
                                position: 'absolute',
                                left: -5,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 10,
                                height: 18,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'w-resize',
                                borderRadius: 1,
                                zIndex: 5,
                                touchAction: 'none',
                              }}
                            />
                            {/* Corners */}
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-nw')}
                              sx={{
                                position: 'absolute',
                                left: -6,
                                top: -6,
                                width: 12,
                                height: 12,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'nw-resize',
                                borderRadius: '50%',
                                zIndex: 6,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-ne')}
                              sx={{
                                position: 'absolute',
                                right: -6,
                                top: -6,
                                width: 12,
                                height: 12,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'ne-resize',
                                borderRadius: '50%',
                                zIndex: 6,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-sw')}
                              sx={{
                                position: 'absolute',
                                left: -6,
                                bottom: -6,
                                width: 12,
                                height: 12,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'sw-resize',
                                borderRadius: '50%',
                                zIndex: 6,
                                touchAction: 'none',
                              }}
                            />
                            <Box
                              onPointerDown={(e) => handlePointerDown(e, el.id, 'resize-se')}
                              sx={{
                                position: 'absolute',
                                right: -6,
                                bottom: -6,
                                width: 12,
                                height: 12,
                                bgcolor: '#1976d2',
                                border: '2px solid #fff',
                                cursor: 'se-resize',
                                borderRadius: '50%',
                                zIndex: 6,
                                touchAction: 'none',
                              }}
                            />
                          </>
                        )}
                        
                        {renderCanvasElementContent(el)}
                      </Box>
                    );
                  })}
              </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">Välj eller skapa ett produktblock</Typography>
          </Box>
        )}
      </Paper>

      {/* Right panel - Properties with Accordions */}
      <Paper
        sx={{
          ...PANEL_SX,
          width: { xs: '100%', lg: rightPanelWidth },
          minWidth: { lg: 280 },
          maxWidth: { lg: 720 },
          flexShrink: 0,
          minHeight: 0,
          overflow: 'auto',
          maxHeight: { xs: 680, lg: 'none' },
          position: 'relative',
        }}
      >
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            rightPanelResizeRef.current = { startX: e.clientX, startWidth: rightPanelWidth };
            setIsResizingRightPanel(true);
          }}
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            zIndex: 20,
            '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
          }}
        />
        {selectedBlock && (
          <>
            {/* Elements Accordion */}
            <Accordion
              expanded={expandedElements}
              onChange={(_, expanded) => setExpandedElements(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Element</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {(Object.keys(ELEMENT_ICONS) as PdfElementKind[]).map((kind) => (
                    <Tooltip key={kind} title={ELEMENT_KIND_LABELS[kind]}>
                      <Button size="small" variant="outlined" onClick={() => handleAddElement(kind)}>
                        {ELEMENT_ICONS[kind]}
                      </Button>
                    </Tooltip>
                  ))}
                </Box>
                <Divider sx={{ mb: 1 }} />
                <List dense>
                  {selectedBlock.elements.map((el) => (
                    <ListItem
                      key={el.id}
                      disablePadding
                      secondaryAction={
                        <IconButton size="small" onClick={() => handleDeleteElement(el.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        selected={selectedElement?.id === el.id}
                        onClick={() => {
                          setSelectedElement(el);
                          setExpandedProperties(true);
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>{ELEMENT_ICONS[el.kind]}</ListItemIcon>
                        <ListItemText
                          primary={ELEMENT_KIND_LABELS[el.kind] || el.kind}
                          secondary={el.binding?.metadataKey || el.content?.substring(0, 20) || ''}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Element Properties Accordion */}
            {selectedElement && (
              <Accordion
                expanded={expandedProperties}
                onChange={(_, expanded) => setExpandedProperties(expanded)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Egenskaper</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Position & Storlek
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="X"
                        type="number"
                        size="small"
                        value={selectedElement.xMm}
                        onChange={(e) => updateElement(selectedElement.id, { xMm: Number(e.target.value) })}
                        sx={{ width: '25%' }}
                      />
                      <TextField
                        label="Y"
                        type="number"
                        size="small"
                        value={selectedElement.yMm}
                        onChange={(e) => updateElement(selectedElement.id, { yMm: Number(e.target.value) })}
                        sx={{ width: '25%' }}
                      />
                      <TextField
                        label="B"
                        type="number"
                        size="small"
                        value={selectedElement.wMm}
                        onChange={(e) => updateElement(selectedElement.id, { wMm: Number(e.target.value) })}
                        sx={{ width: '25%' }}
                      />
                      <TextField
                        label="H"
                        type="number"
                        size="small"
                        value={selectedElement.hMm}
                        onChange={(e) => updateElement(selectedElement.id, { hMm: Number(e.target.value) })}
                        sx={{ width: '25%' }}
                      />
                    </Box>

                    {selectedElement.kind === 'textField' && (
                      <>
                        <Divider />
                        <Typography variant="caption" color="text.secondary">
                          Databindning
                        </Typography>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Metadatafält</InputLabel>
                          <Select
                            value={selectedElement.binding?.metadataKey || ''}
                            label="Metadatafält"
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                binding: { ...selectedElement.binding, metadataKey: String(e.target.value || '') },
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>Välj fält…</em>
                            </MenuItem>
                            {availableFields.map((field) => (
                              <MenuItem key={field.id} value={field.id}>
                                {field.label} ({field.templateName})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {fieldTemplatesLoading && (
                          <Typography variant="caption" color="text.secondary">
                            Laddar fält…
                          </Typography>
                        )}
                        <TextField
                          label="Metadatafält (manuell)"
                          size="small"
                          fullWidth
                          value={selectedElement.binding?.metadataKey || ''}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              binding: { ...selectedElement.binding, metadataKey: e.target.value },
                            })
                          }
                          placeholder="t.ex. name, ean, weight"
                        />
                        <TextField
                          label="Prefix"
                          size="small"
                          fullWidth
                          value={selectedElement.binding?.prefix || ''}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              binding: { ...selectedElement.binding, prefix: e.target.value },
                            })
                          }
                        />
                        <TextField
                          label="Suffix"
                          size="small"
                          fullWidth
                          value={selectedElement.binding?.suffix || ''}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              binding: { ...selectedElement.binding, suffix: e.target.value },
                            })
                          }
                        />
                        <TextField
                          label="Fallback"
                          size="small"
                          fullWidth
                          value={selectedElement.binding?.fallbackValue || ''}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              binding: { ...selectedElement.binding, fallbackValue: e.target.value },
                            })
                          }
                        />
                      </>
                    )}

                    {selectedElement.kind === 'staticText' && (
                      <>
                        <Divider />
                        <TextField
                          label="Text"
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={selectedElement.content || ''}
                          onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                        />
                      </>
                    )}

                    {selectedElement.kind === 'richText' && (
                      <>
                        <Divider />
                        <Alert severity="info" sx={{ mb: 1 }}>
                          Stödjer vanlig HTML med inline-styles. Enkla `style`-block med klassnamn försöker konverteras automatiskt, men externa fonter, script, pseudo-element och avancerad CSS stöds inte fullt ut.
                        </Alert>
                        <TextField
                          label="Rich text (HTML)"
                          size="small"
                          fullWidth
                          multiline
                          rows={5}
                          value={selectedElement.content || ''}
                          onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                          helperText="Stöd för {{metadataKey}} och även {metadataKey}. Klistra helst in semantiskt HTML-innehåll, inte hela webblayouter."
                        />
                      </>
                    )}

                    {selectedElement.kind === 'bulletList' && (
                      <>
                        <Divider />
                        <TextField
                          label="Punktlista"
                          size="small"
                          fullWidth
                          multiline
                          rows={4}
                          value={selectedElement.content || ''}
                          onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                          helperText="En punkt per rad. Inledande bullets tas bort automatiskt."
                        />
                      </>
                    )}

                    {selectedElement.kind === 'staticImage' && (
                      <>
                        <Divider />
                        <Autocomplete
                          size="small"
                          options={staticImageOptions}
                          getOptionLabel={(option) => option.title || option.fileName || ''}
                          inputValue={staticImageSearch}
                          onInputChange={(_, value) => setStaticImageSearch(value)}
                          onChange={(_, asset) => {
                            if (asset) {
                              updateElement(selectedElement.id, { imageUrl: `/api/assets/${asset._id}/download` });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Sök bild i DAM" placeholder="Titel eller filnamn..." />
                          )}
                        />
                        <TextField
                          label="Bild-URL"
                          size="small"
                          fullWidth
                          value={selectedElement.imageUrl || ''}
                          onChange={(e) => updateElement(selectedElement.id, { imageUrl: e.target.value })}
                          helperText="Använd helst en DAM-bild eller /uploads/… för säker PDF-rendering."
                        />
                      </>
                    )}

                    <Divider />
                    <Typography variant="caption" color="text.secondary">
                      Typografi
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Typsnitt</InputLabel>
                      <Select
                        value={selectedElement.style.fontFamily || 'Arial'}
                        label="Typsnitt"
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, fontFamily: e.target.value as any },
                          })
                        }
                      >
                        <MenuItem value="Arial">Arial</MenuItem>
                        <MenuItem value="Kapra Neue">Kapra Neue</MenuItem>
                        <MenuItem value="Oswald">Oswald</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Storlek (pt)"
                        type="number"
                        size="small"
                        value={selectedElement.style.fontSize || 12}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, fontSize: Math.max(0.5, Number(e.target.value) || 0.5) },
                          })
                        }
                        inputProps={{ min: 0.5, step: 0.5 }}
                        sx={{ width: '50%' }}
                      />
                      <TextField
                        label="Färg"
                        type="color"
                        size="small"
                        value={selectedElement.style.color || '#000000'}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, color: e.target.value },
                          })
                        }
                        sx={{ width: '50%' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Radavstånd"
                        type="number"
                        size="small"
                        value={selectedElement.style.lineHeight || 1.4}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, lineHeight: Number(e.target.value) || 1.4 },
                          })
                        }
                        sx={{ width: '50%' }}
                        inputProps={{ step: 0.1, min: 0.8 }}
                      />
                      <TextField
                        label="Teckenmellanrum (pt)"
                        type="number"
                        size="small"
                        value={selectedElement.style.letterSpacing || 0}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, letterSpacing: Number(e.target.value) || 0 },
                          })
                        }
                        sx={{ width: '50%' }}
                        inputProps={{ step: 0.1 }}
                      />
                    </Box>
                    <TextField
                      label="Innerpadding (mm)"
                      type="number"
                      size="small"
                      fullWidth
                      value={selectedElement.style.padding || 0}
                      onChange={(e) =>
                        updateElement(selectedElement.id, {
                          style: { ...selectedElement.style, padding: Number(e.target.value) || 0 },
                        })
                      }
                      inputProps={{ step: 0.5, min: 0 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <FormControl size="small" sx={{ width: '50%' }}>
                        <InputLabel>Textjustering</InputLabel>
                        <Select
                          value={selectedElement.style.textAlign || 'left'}
                          label="Textjustering"
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              style: { ...selectedElement.style, textAlign: e.target.value as 'left' | 'center' | 'right' },
                            })
                          }
                        >
                          <MenuItem value="left">Vänster</MenuItem>
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="right">Höger</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ width: '50%' }}>
                        <InputLabel>Vertikal</InputLabel>
                        <Select
                          value={selectedElement.style.verticalAlign || 'top'}
                          label="Vertikal"
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              style: { ...selectedElement.style, verticalAlign: e.target.value as 'top' | 'middle' | 'bottom' },
                            })
                          }
                        >
                          <MenuItem value="top">Topp</MenuItem>
                          <MenuItem value="middle">Mitten</MenuItem>
                          <MenuItem value="bottom">Botten</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Padding (mm)"
                        type="number"
                        size="small"
                        value={selectedElement.style.padding || 0}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, padding: Number(e.target.value) || 0 },
                          })
                        }
                        sx={{ width: '50%' }}
                      />
                      <TextField
                        label="Opacitet"
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 1, step: 0.1 }}
                        value={selectedElement.style.opacity ?? 1}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, opacity: Number(e.target.value) },
                          })
                        }
                        sx={{ width: '50%' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Bakgrund"
                        type="color"
                        size="small"
                        value={selectedElement.style.backgroundColor || '#ffffff'}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, backgroundColor: e.target.value },
                          })
                        }
                        sx={{ width: '50%' }}
                      />
                      <TextField
                        label="Radie (mm)"
                        type="number"
                        size="small"
                        value={selectedElement.style.borderRadius || 0}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, borderRadius: Number(e.target.value) || 0 },
                          })
                        }
                        sx={{ width: '50%' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Kantbredd (mm)"
                        type="number"
                        size="small"
                        value={selectedElement.style.borderWidth || 0}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, borderWidth: Number(e.target.value) || 0 },
                          })
                        }
                        inputProps={{ min: 0, step: 0.25 }}
                        sx={{ width: '34%' }}
                      />
                      <FormControl size="small" sx={{ width: '33%' }}>
                        <InputLabel>Kantstil</InputLabel>
                        <Select
                          value={selectedElement.style.borderStyle || 'solid'}
                          label="Kantstil"
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              style: { ...selectedElement.style, borderStyle: e.target.value as 'solid' | 'dashed' | 'dotted' },
                            })
                          }
                        >
                          <MenuItem value="solid">Solid</MenuItem>
                          <MenuItem value="dashed">Streckad</MenuItem>
                          <MenuItem value="dotted">Prickad</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Kantfärg"
                        type="color"
                        size="small"
                        value={selectedElement.style.borderColor || '#000000'}
                        onChange={(e) =>
                          updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, borderColor: e.target.value },
                          })
                        }
                        sx={{ width: '33%' }}
                      />
                    </Box>

                    {(selectedElement.kind === 'textField' || selectedElement.kind === 'staticText' || selectedElement.kind === 'richText') && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!selectedElement.style.autoFit}
                              onChange={(e) =>
                                updateElement(selectedElement.id, {
                                  style: { ...selectedElement.style, autoFit: e.target.checked },
                                })
                              }
                            />
                          }
                          label="Auto-fit text"
                        />
                        {selectedElement.style.autoFit && (
                          <TextField
                            label="Min fontstorlek (pt)"
                            type="number"
                            size="small"
                            fullWidth
                            value={selectedElement.style.minFontSize || 6}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                style: { ...selectedElement.style, minFontSize: Number(e.target.value) || 6 },
                              })
                            }
                          />
                        )}
                      </>
                    )}

                    {selectedElement.kind === 'productImage' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Bildjustering</InputLabel>
                        <Select
                          value={selectedElement.style.imageAlign || 'center'}
                          label="Bildjustering"
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              style: { ...selectedElement.style, imageAlign: e.target.value as 'top' | 'center' | 'bottom' },
                            })
                          }
                        >
                          <MenuItem value="top">Topp</MenuItem>
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="bottom">Botten</MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    {selectedElement.kind === 'bulletList' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Bullet-stil</InputLabel>
                        <Select
                          value={selectedElement.style.bulletStyle || 'disc'}
                          label="Bullet-stil"
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              style: { ...selectedElement.style, bulletStyle: e.target.value as any },
                            })
                          }
                        >
                          <MenuItem value="disc">Disc</MenuItem>
                          <MenuItem value="circle">Circle</MenuItem>
                          <MenuItem value="square">Square</MenuItem>
                          <MenuItem value="none">Ingen</MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Rotation"
                        type="number"
                        size="small"
                        value={selectedElement.rotation || 0}
                        onChange={(e) => updateElement(selectedElement.id, { rotation: Number(e.target.value) || 0 })}
                        sx={{ width: '50%' }}
                      />
                      <TextField
                        label="Lager (z)"
                        type="number"
                        size="small"
                        value={selectedElement.zIndex || 0}
                        onChange={(e) => updateElement(selectedElement.id, { zIndex: Number(e.target.value) || 0 })}
                        sx={{ width: '50%' }}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Block Size Presets */}
            <Accordion
              expanded={expandedPresets}
              onChange={(_, expanded) => setExpandedPresets(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Storlek (presets)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(BLOCK_SIZE_PRESETS).map(([key, preset]) => (
                    <Button
                      key={key}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        updateBlockField('wMm', preset.wMm);
                        updateBlockField('hMm', preset.hMm);
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, block: null })}>
        <DialogTitle>Radera block?</DialogTitle>
        <DialogContent>
          <Typography>
            Är du säker på att du vill radera blocket "{deleteDialog.block?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, block: null })}>Avbryt</Button>
          <Button onClick={handleDeleteBlock} color="error" variant="contained">
            Radera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductBlockEditor;
